import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import LoadingSvg from '@/components/common/LoadingSvg';
import { Cookie } from '@/tscapi/utils/cookie';

import { EmptyBag } from './EmptyBag';
import { FreeShippingNotice } from './FreeShippingNotice';
import { useAuthenticated } from './hooks/useAuthenticated';
import { useCart, useUpdateCart } from './hooks/useCart';
import { useOrder } from './hooks/useOrder';
import { en, fr } from './i18n';
import Items from './Items';
import { ModifyOrderHero } from './ModifyOrderHero';
import { Notices } from './Notices';
import OrderSummary from './OrderSummary';
import { getShowBagEmpty } from './utils/isBagEmpty';

export const ShoppingBag = () => {
    //state
    const cookie: Cookie = new Cookie();
    const [isModifyOrder, setIsModifyOrder] = useState(false);
    const [isCheckoutDisabled, setIsCheckoutDisabled] = useState(false);
    const [isFraud, setIsFraud] = useState(false);
    const [isPaypalDisabled, setIsPaypalDisabled] = useState(false);
    const [isBagEmpty, setIsBagEmpty] = useState(true);
    const [easyPayWarningBlockCheckout, setEasyWarningBlockCheckout] = useState(false);
    const [showEasyPayNotice, setShowEasyPayNotice] = useState(false);
    //translation
    const { i18n, t } = useTranslation('shoppingbag');
    i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
    i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);
    //react query data & hooks
    const isAuthenticated = useAuthenticated();
    const cart = useCart(isAuthenticated);
    const isMutating = useIsMutating();
    const updateMutation = useUpdateCart(cookie?.UserAccessToken?.Edp, cookie?.CartGuid);
    const order = useOrder(isAuthenticated, cookie.ModifyOrderNumber);

    useEffect(() => {
        //check the bag if modify order mode
        if (cookie.ModifyOrderNumber != undefined && cookie.ModifyOrderNumber.length > 0) {
            setIsModifyOrder(true);
        } else {
            setIsModifyOrder(false);
        }
    }, [cookie.ModifyOrderNumber]);

    useEffect(() => {
        setIsBagEmpty(getShowBagEmpty(cart.data));
        if (cart?.data?.OrderSummary?.EasyPay != undefined) {
            setIsPaypalDisabled(true);
        } else {
            setIsPaypalDisabled(false);
        }
    }, [cart]);

    // if (cookie.ModifyOrderNumber != undefined && cookie.ModifyOrderNumber.length > 0 && !isModifyOrder) {
    //     setIsModifyOrder(true);
    // } else {
    //     if (isModifyOrder) {
    //         setIsModifyOrder(false);
    //     }
    // }

    // if (isModifyOrder) {
    //     console.log('modify order mode');
    // } else {
    //     console.log('is not modify order mode');
    //     cookie.ModifyOrderNumber = 'Z31987850000';
    // }
    // cookie.ModifyOrderNumber = 'Z31987850000';
    // if (isFetching) {
    //     console.warn('isFetching');
    // }
    // if (updateMutation.isLoading) {
    //     console.warn('loading');
    // }
    // if (isMutating) {
    //     console.warn('ismutating');
    // }

    return (
        <div className="bag__wrapper mx-auto w-full max-w-[1200px]">
            <ModifyOrderHero isBagEmpty={isBagEmpty} isModifyOrder={isModifyOrder} />
            <div className="bag__details">
                {isBagEmpty && !isModifyOrder && <EmptyBag isAuth={isAuthenticated} />}
                {!isBagEmpty && !isModifyOrder && (
                    <>
                        <h1 className="bag__title">{t('Your Shopping Bag')}</h1>
                        <p className="bag__para--full mb-2.5">
                            {t(
                                'Popular items sell out fast. Adding an item to your bag doesn’t hold it. Get what you love before it’s gone.',
                            )}
                        </p>
                    </>
                )}

                {!isModifyOrder && (
                    <>
                        {/* CHECKOUT BUTTON MOBILE ONLY */}
                        {cart.data?.OrderSummary?.TotalQuantity > 0 && (
                            <button
                                className="summary__button button__black mobileOnly !max-w-screen-md3"
                                type="button"
                                disabled={isCheckoutDisabled}
                                onClick={() => {
                                    if (isFraud || isCheckoutDisabled) {
                                        return;
                                    }
                                    if (!easyPayWarningBlockCheckout) {
                                        window.location.href = '/pages/expresscheckout';
                                    } else {
                                        setShowEasyPayNotice(true);
                                    }
                                    //window.location.href = '/pages/expresscheckout';
                                }}
                                aria-label={t('Proceed to checkout')}
                            >
                                <span>
                                    {t('Proceed to checkout')} ({cart.data?.OrderSummary?.TotalQuantity}{' '}
                                    {order?.data?.OrderSummary.Quantity + cart.data?.OrderSummary?.TotalQuantity < 2
                                        ? t('item')
                                        : t('items')}
                                    )
                                </span>
                            </button>
                        )}
                    </>
                )}
                {/* freeshipping notice */}
                <FreeShippingNotice />
                {/* Notices */}
                <Notices
                    setIsCheckoutDisabled={setIsCheckoutDisabled}
                    isModifyOrder={isModifyOrder}
                    setIsPaypalDisabled={setIsPaypalDisabled}
                    setIsFraud={setIsFraud}
                />
                {/* List of products in bag */}
                <Items isModifyOrder={isModifyOrder} isBagEmpty={isBagEmpty} />
                {!isModifyOrder && (
                    <a href="/" className="bag__back-button">
                        {t('Back to shopping')}
                    </a>
                )}
            </div>
            <div className="bag__summary">
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <OrderSummary
                        isModifyOrder={isModifyOrder}
                        isCheckoutDisabled={isCheckoutDisabled}
                        isFraud={isFraud}
                        isPaypalDisabled={isPaypalDisabled}
                        setIsPaypalDisabled={setIsPaypalDisabled}
                        setIsCheckoutDisabled={setIsCheckoutDisabled}
                        easyPayWarningBlockCheckout={easyPayWarningBlockCheckout}
                        setEasyWarningBlockCheckout={setEasyWarningBlockCheckout}
                        showEasyPayNotice={showEasyPayNotice}
                        setShowEasyPayNotice={setShowEasyPayNotice}
                    />
                </ErrorBoundary>
            </div>{' '}
            {updateMutation.isLoading || isMutating || cart.isLoading ? (
                <div className="blockPageWrap" style={{ display: 'block' }}>
                    <div className="blockPageOverlay"></div>
                    <div className="blockMsg">
                        <LoadingSvg />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
const ErrorFallback = () => {
    return (
        <div className="summary" style={{ background: '#fff' }}>
            <div className="summary__loading">
                <div style={{ textAlign: 'center', padding: '15px 24px 0' }}>
                    Error loading the Order Summary.
                    <br /> Please reload the page.
                </div>
            </div>
        </div>
    );
};
